/**
 * QI1.3 / QE1.3 transaction sample selection.
 *
 * Rules (agreed):
 * 1. |amount| >= performance materiality → always include
 * 2. Remaining: round-robin by calendar month, largest abs amount first each month
 * 3. Stop when selected abs-amount sum >= population abs-amount × coverageRatio (default 10%)
 * 4. Months with no invoices are skipped
 */
import {buildGlDetailDescription} from '@/pages/components/audit/adjustment/adjust/dialogs/common/buildGlDetailDescription';

export type SampleInvoiceCandidate = {
    invoiceId?: string;
    date: string | null;
    invoiceNo?: string | null;
    voucherNo?: string | null;
    invoiceDate?: string | null;
    chequeNo?: string | null;
    description?: string | null;
    details?: string | null;
    amount: number;
};

export type SelectTransactionSampleOptions = {
    /** Performance materiality threshold for 100% testing. */
    performanceMateriality: number;
    /**
     * coverage: stop when selected abs-amount sum reaches population × coverageRatio (default).
     * count: stop when total selected items reach maxCount (≥PM items always included first).
     */
    mode?: 'coverage' | 'count';
    /** Target coverage of population monetary value (default 0.10). Used when mode is coverage. */
    coverageRatio?: number;
    /** Target number of items to select. Used when mode is count. */
    maxCount?: number;
};

export type SelectTransactionSampleResult = {
    selected: SampleInvoiceCandidate[];
    populationTotal: number;
    targetAmount: number;
    selectedTotal: number;
    abovePmCount: number;
    residualPickedCount: number;
};

function absAmount(n: number): number {
    return Math.abs(Number(n) || 0);
}

/** Parse invoice date to YYYY-MM; returns null if unparseable. */
export function monthKeyFromDate(date: string | null | undefined): string | null {
    if (!date) return null;
    const s = String(date).trim();
    // ISO / yyyy-MM-dd / yyyy/MM/dd
    const m1 = s.match(/^(\d{4})[-/](\d{1,2})/);
    if (m1) return `${m1[1]}-${m1[2].padStart(2, '0')}`;
    // dd/MM/yyyy
    const m2 = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})/);
    if (m2) return `${m2[3]}-${m2[2].padStart(2, '0')}`;
    const t = Date.parse(s);
    if (!Number.isNaN(t)) {
        const d = new Date(t);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    }
    return null;
}

export function selectTransactionSample(
    invoices: SampleInvoiceCandidate[],
    options: SelectTransactionSampleOptions,
): SelectTransactionSampleResult {
    const pm = Math.max(0, Number(options.performanceMateriality) || 0);
    const mode = options.mode ?? 'coverage';
    const coverageRatio = options.coverageRatio ?? 0.1;
    const maxCount = Math.max(0, Math.floor(Number(options.maxCount) || 0));

    const pool = (invoices || []).filter((inv) => absAmount(inv.amount) > 0);
    const populationTotal = pool.reduce((s, inv) => s + absAmount(inv.amount), 0);
    const targetAmount = mode === 'coverage' ? populationTotal * coverageRatio : 0;

    if (pool.length === 0 || (mode === 'coverage' && targetAmount <= 0) || (mode === 'count' && maxCount <= 0)) {
        return {
            selected: [],
            populationTotal,
            targetAmount,
            selectedTotal: 0,
            abovePmCount: 0,
            residualPickedCount: 0,
        };
    }

    const abovePm: SampleInvoiceCandidate[] = [];
    const residual: SampleInvoiceCandidate[] = [];
    for (const inv of pool) {
        if (absAmount(inv.amount) >= pm && pm > 0) {
            abovePm.push(inv);
        } else {
            residual.push(inv);
        }
    }

    const selected: SampleInvoiceCandidate[] = [...abovePm];
    let selectedTotal = abovePm.reduce((s, inv) => s + absAmount(inv.amount), 0);
    const abovePmCount = abovePm.length;

    if (mode === 'coverage' && selectedTotal >= targetAmount) {
        return finalize(selected, populationTotal, targetAmount, selectedTotal, abovePmCount);
    }

    if (mode === 'count' && selected.length >= maxCount) {
        return finalize(selected, populationTotal, targetAmount, selectedTotal, abovePmCount);
    }

    // Group residual by month; skip undated into a synthetic bucket after dated months
    const byMonth = new Map<string, SampleInvoiceCandidate[]>();
    const undated: SampleInvoiceCandidate[] = [];
    for (const inv of residual) {
        const key = monthKeyFromDate(inv.date);
        if (!key) {
            undated.push(inv);
            continue;
        }
        const list = byMonth.get(key) || [];
        list.push(inv);
        byMonth.set(key, list);
    }

    const monthKeys = Array.from(byMonth.keys()).sort();
    if (undated.length > 0) {
        byMonth.set('__undated__', undated);
        monthKeys.push('__undated__');
    }

    for (const key of monthKeys) {
        byMonth.get(key)!.sort((a, b) => absAmount(b.amount) - absAmount(a.amount));
    }

    const cursors = new Map<string, number>();
    monthKeys.forEach((k) => cursors.set(k, 0));

    let residualPickedCount = 0;
    let progressed = true;
    while (progressed) {
        if (mode === 'coverage' && selectedTotal >= targetAmount) break;
        if (mode === 'count' && selected.length >= maxCount) break;

        progressed = false;
        for (const key of monthKeys) {
            if (mode === 'coverage' && selectedTotal >= targetAmount) break;
            if (mode === 'count' && selected.length >= maxCount) break;
            const list = byMonth.get(key)!;
            const idx = cursors.get(key) || 0;
            if (idx >= list.length) continue;
            const pick = list[idx];
            cursors.set(key, idx + 1);
            selected.push(pick);
            selectedTotal += absAmount(pick.amount);
            residualPickedCount += 1;
            progressed = true;
        }
    }

    return finalize(selected, populationTotal, targetAmount, selectedTotal, abovePmCount, residualPickedCount);
}

function finalize(
    selected: SampleInvoiceCandidate[],
    populationTotal: number,
    targetAmount: number,
    selectedTotal: number,
    abovePmCount: number,
    residualPickedCount = 0,
): SelectTransactionSampleResult {
    const sorted = [...selected].sort((a, b) => {
        const da = a.date || '';
        const db = b.date || '';
        if (da !== db) return da < db ? -1 : 1;
        return absAmount(b.amount) - absAmount(a.amount);
    });
    return {
        selected: sorted,
        populationTotal,
        targetAmount,
        selectedTotal,
        abovePmCount,
        residualPickedCount,
    };
}

export function candidatesToTestRows(
    selected: SampleInvoiceCandidate[],
    annualId: number,
): Array<{id: number; annualId: number; date: string | null; invNo: string; description: string; amount: number | null}> {
    return selected.map((inv, i) => ({
        id: i,
        annualId,
        date: inv.date,
        invNo: (inv.invoiceNo || inv.voucherNo || '').trim(),
        description: buildGlDetailDescription(inv),
        // Transaction test amounts are shown as absolute positive values
        amount: Math.abs(Number(inv.amount) || 0),
    }));
}
